import { HeaderSearchSelection } from '@upright/common/model/search/types';
import { uniq } from 'lodash';
import { useRef, useState } from 'react';
import { ModelEntityReference } from '../../../../../../common/model/input/types';
import { usePostData } from '../../../../../api/common/hooks';
import { sendSearchAttempts } from '../../../../../api/model/search';
import { useGetLoggedInUser } from '../../../../generic/hooks';
import { getTeamBrowser } from '../../../../generic/utilities/storage';
import { usePushAnonymousGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';

// This hook sets up tracking for the written input, saving search queries in an array. These attempts
// are sent to server for analysis purposes. Kept around at 8/2022 after discussing with WN. -ML

// TODO ML: This is called in ALL search components with opt-out prop, probably not useful to us.
// TODO ML: It makes no sense that this hook receives header-search specific UI selection: take in an array of search objects instead
// TODO ML: Sending the user id with search input is super problematic without consent and probably unnecessary for analysis, replace with isTeam flag or similar.

export function useTrackableSearchInputs(disableSending: boolean | undefined) {
  /**
   * ET note on non-trivial logic: there is no easy way to differentiate between blur events due to user unfocusing
   * from the search bar (failed search) and the component getting reset and/or re-rendered as empty right after the
   * user clicks on a search hit (successful search).
   *
   * useRef hooks are used in order to maintain state between such re-renders. The mega-hacky pause hook is used to
   * ignore onBlur-triggered duplicate calls immediately after the user clicks on a search hit.
   */
  const [input, _setInput] = useState<string>('');
  const [wasEdited, setWasEdited] = useState(false); // Used to only send attempts after edits
  const attempts = useRef<string[]>([]);
  const prevLengthDiff = useRef(0);
  const { pause, paused } = usePause();
  const usePost = usePostData<undefined>();
  const user = useGetLoggedInUser();
  const pushGtmEvent = usePushAnonymousGtmEvent();

  const setInput = (newValue: string) => {
    const currentValue = input;
    attempts.current = saveInputAttempt(newValue, currentValue, prevLengthDiff.current, attempts.current);
    prevLengthDiff.current = newValue.length - currentValue.length;
    setWasEdited(true);
    _setInput(newValue);
  };

  const sendAttempts = (details?: {
    entityRef?: ModelEntityReference;
    headerSearchSelection?: HeaderSearchSelection;
  }) => {
    if (!wasEdited || disableSending || (!user && getTeamBrowser())) {
      // Do not send if disabled, or if team is logged out (indisinguishable from visitor searches)
      return;
    }

    const validEntityRef = details?.entityRef && !input.trim() ? undefined : details?.entityRef; // skip suggestions clicks
    const attemptsToSend = filterOutPartials([input, ...attempts.current], !!validEntityRef);
    if (attemptsToSend.length && !paused.current) {
      const payload = {
        attempts: attemptsToSend,
        searchType: details?.headerSearchSelection,
        entityRef: validEntityRef,
      };
      const gtmPayload = {
        search: attemptsToSend.shift() || '',
        entityRef: validEntityRef,
        searchType: details?.headerSearchSelection,
      };
      setWasEdited(false);
      pause();
      pushGtmEvent({ event: CustomGtmEvent.IMPACT_PROFILE_SEARCH, data: gtmPayload });
      void usePost.post(sendSearchAttempts(payload));
      prevLengthDiff.current = 0;
      attempts.current = [];
    }
  };

  /**
   * Forget what was typed without sending it, for a search that ended in something other than an
   * entity: an attempt sent without an entity is recorded as a search that found nothing, which a
   * pick of another kind (the unified shell's "Ask Upright AI" row or an internal page) is not.
   * Call it after clearing the input, which itself records the cleared text as an attempt.
   */
  const discardAttempts = () => {
    attempts.current = [];
    prevLengthDiff.current = 0;
    setWasEdited(false);
  };

  return {
    input,
    setInput,
    sendAttempts,
    discardAttempts,
  };
}

function saveInputAttempt(value: string, previousValue: string, lastLengthDiff: number, lastAttempts: string[]) {
  const trimmed = value.trim();
  const trimmedPrev = previousValue.trim();
  if (trimmed.length < trimmedPrev.length && lastLengthDiff > 0) {
    // User starts deleting characters
    return [trimmedPrev, ...lastAttempts];
  } else if (trimmed.length === trimmedPrev.length && trimmed !== trimmedPrev) {
    // User copy pasted (or we skipped a beat)
    return [trimmedPrev, ...lastAttempts];
  } else {
    return lastAttempts;
  }
}

function filterOutPartials(capturedAttempts: string[], spareFirst: boolean) {
  const uniqueAttempts = uniq(capturedAttempts.map(s => s.trim()));
  return uniqueAttempts
    .filter(
      (attempt, iAttempt) =>
        (spareFirst && iAttempt === 0) ||
        !uniqueAttempts.find((other, iOther) => iAttempt !== iOther && other.startsWith(attempt)),
    )
    .filter((attempt, iAttempt) => (spareFirst && iAttempt === 0) || attempt.length > 2);
}

function usePause() {
  const paused = useRef(false);
  const pause = () => {
    paused.current = true;
    setTimeout(() => {
      paused.current = false;
    }, 1000);
  };
  return { paused, pause };
}
